import {
  type CheckoutQuoteResponse,
  type CryptoWithdrawalOrder,
  type WalletExecutionErrorCode,
  createWithdrawalSubmissionId,
  ensureRelayClientInitialized,
  isUserRejectedError,
  processCryptoWithdrawalOrder,
  toWalletExecutionErrorCode,
} from '@funkit/connect-core'
import { useCallback, useRef, useState } from 'react'
import type { Address } from 'viem'

import type {
  WithdrawalFlowCallbacks,
  WithdrawalFlowConfig,
  WithdrawalToken,
} from '../flow/withdrawalFlowConfig'
import { useFunkitIdentity } from '../identity/IdentityContext'
import { logger } from '../logging/logger'
import { WALLETLESS_WITHDRAWAL_ERROR } from './walletlessWithdrawal'

// Module-scoped because the hook and its refs unmount when the sheet closes
// while the transfer is still running: a reopen mounts a fresh hook whose own
// ref guard knows nothing about the signature already in flight.
const inFlightWithdrawals = new Set<string>()

/** What makes two presses the same withdrawal rather than two of them. */
function withdrawalKey({
  walletAddress,
  recipientAddress,
  amount,
  sourceToken,
  destinationToken,
}: {
  walletAddress: string
  recipientAddress: string
  amount: string
  sourceToken: WithdrawalToken
  destinationToken: WithdrawalToken
}): string {
  return [
    walletAddress,
    recipientAddress,
    amount,
    sourceToken.chainId,
    sourceToken.address,
    destinationToken.chainId,
    destinationToken.address,
  ]
    .join('|')
    .toLowerCase()
}

/**
 * @internal Test-only: clear the process-wide in-flight set so suites that
 * reuse withdrawal details stay isolated. Not part of the public API.
 */
export function __resetInFlightCryptoWithdrawals(): void {
  inFlightWithdrawals.clear()
}

/**
 * What a submitted withdrawal can be followed by: a venue that submits
 * off-chain leaves an address, not a transaction of ours. A union rather than
 * two optionals, so the completion screen cannot read the wrong one.
 */
export type DepositAddressOrHash =
  | { kind: 'transaction'; txHash: string }
  | { kind: 'depositAddress'; depositAddress: Address }

/**
 * Stands in when a venue submits but reports no address back. Matches web's
 * `generateSyntheticDepositAddress`, so one sentinel means the same thing on
 * both platforms.
 */
const SYNTHETIC_DEPOSIT_ADDRESS =
  '0x0000000000000000000000000000000000000001' as const

export type CryptoWithdrawalSubmission =
  | { status: 'idle' }
  | { status: 'submitting'; stepMessage?: string }
  | {
      status: 'submitted'
      depositAddressOrHash: DepositAddressOrHash
      submissionId: string
      /** The amount that executed, not whatever the field says by now. */
      amount: string
    }
  | {
      status: 'failed'
      code: WalletExecutionErrorCode
      /**
       * The error's own text. Read by the form only where the classified cause
       * says nothing the user can act on — a failure the SDK cannot name is
       * better reported in the provider's words than as "please try again".
       */
      message: string
    }

export interface UseCryptoWithdrawalExecutionParams {
  config: WithdrawalFlowConfig
  sourceToken: WithdrawalToken
  destinationToken: WithdrawalToken
  callbacks: WithdrawalFlowCallbacks
}

/**
 * Runs the withdrawal the user approved, down whichever rail the config names:
 * the relay quote, or `config.submitWithdrawal` when the venue moves the funds.
 *
 * Both guards are read synchronously inside the async submit — before the first
 * await — so neither may wait for a re-render: a ref for a double-tap on this
 * mount, and a module-scoped set of in-flight withdrawals for a second press
 * after the sheet was closed and reopened mid-execution. The set holds only
 * while the signature is outstanding; deliberately sending the same amount to
 * the same address again once it has settled is a legitimate withdrawal.
 */
export function useCryptoWithdrawalExecution({
  config,
  sourceToken,
  destinationToken,
  callbacks,
}: UseCryptoWithdrawalExecutionParams) {
  const { apiKey, userId } = useFunkitIdentity()
  const isSubmittingRef = useRef(false)
  const [submission, setSubmission] = useState<CryptoWithdrawalSubmission>({
    status: 'idle',
  })

  const submit = useCallback(
    async ({
      quote,
      recipientAddress,
      amount,
    }: {
      /** The quote on screen — executed as-is, so fees shown are fees paid. */
      quote: CheckoutQuoteResponse
      recipientAddress: string
      amount: string
    }) => {
      if (isSubmittingRef.current) {
        logger.warn('connect-rn:crypto-withdrawal:already_submitting')
        return
      }
      const key = withdrawalKey({
        walletAddress: config.withdrawalWallet?.address() ?? '',
        recipientAddress,
        amount,
        sourceToken,
        destinationToken,
      })
      if (inFlightWithdrawals.has(key)) {
        logger.warn('connect-rn:crypto-withdrawal:already_in_flight')
        return
      }
      isSubmittingRef.current = true
      inFlightWithdrawals.add(key)

      const submissionId = createWithdrawalSubmissionId()
      setSubmission({ status: 'submitting' })

      logger.info('crypto-withdrawal:rail_selected', {
        rail: config.submitWithdrawal ? 'venue' : 'relay',
        submissionId,
        fromChainId: sourceToken.chainId,
        fromToken: sourceToken.address,
        toChainId: destinationToken.chainId,
        toToken: destinationToken.address,
      })

      let depositAddressOrHash: DepositAddressOrHash
      try {
        if (config.submitWithdrawal) {
          // Priced, not executed: nothing of ours goes on-chain here.
          const { depositAddress } = await config.submitWithdrawal({
            quote,
            recipientAddress: recipientAddress as Address,
            sourceToken,
            destinationToken,
            amountTokenUnits: amount,
          })
          depositAddressOrHash = {
            kind: 'depositAddress',
            depositAddress: depositAddress ?? SYNTHETIC_DEPOSIT_ADDRESS,
          }
        } else {
          // The relay rail signs from the host's wallet, so a walletless config
          // has nothing to sign with — fail here rather than inside the rail.
          const { withdrawalWallet } = config
          if (!withdrawalWallet) {
            // Its own event, not just the `execution_failed` the catch logs: a
            // host shipped a config the relay rail can never execute, which is
            // worth alerting on separately from a rail that failed.
            logger.error(
              'connect-rn:crypto-withdrawal:walletless_config',
              new Error(WALLETLESS_WITHDRAWAL_ERROR),
              { submission_id: submissionId },
            )
            throw new Error(WALLETLESS_WITHDRAWAL_ERROR)
          }
          const order: CryptoWithdrawalOrder = {
            submissionId,
            recipientAddress:
              recipientAddress as CryptoWithdrawalOrder['recipientAddress'],
            sourceAmountHumanUnit: amount,
            sourceToken,
            destinationToken,
          }
          ensureRelayClientInitialized(logger, 'funkit-connect-rn')
          const { txHash } = await processCryptoWithdrawalOrder({
            order,
            quote,
            wallet: withdrawalWallet,
            apiKey,
            userId,
            logger,
            onStepMessage: (stepMessage) =>
              setSubmission({ status: 'submitting', stepMessage }),
          })
          depositAddressOrHash = { kind: 'transaction', txHash }
        }
      } catch (error) {
        if (isUserRejectedError(error)) {
          // Declining is recoverable — the form stays as it was so the user can
          // simply press again.
          logger.warn('connect-rn:crypto-withdrawal:user_rejected', {
            submission_id: submissionId,
          })
          setSubmission({ status: 'idle' })
          return
        }
        const message = error instanceof Error ? error.message : String(error)
        logger.error(
          'connect-rn:crypto-withdrawal:execution_failed',
          error as Error,
          { submission_id: submissionId },
        )
        setSubmission({
          status: 'failed',
          code: toWalletExecutionErrorCode(error),
          message,
        })
        callbacks.onWithdrawalError?.({ message })
        return
      } finally {
        isSubmittingRef.current = false
        inFlightWithdrawals.delete(key)
      }

      // Funds have moved — report it outside the try above, so a throw from the
      // host cannot be read as this execution failing. Host callbacks are
      // guarded where they enter the SDK (`guardHostCallbacks`), so this one
      // does not need its own.
      setSubmission({
        status: 'submitted',
        depositAddressOrHash,
        submissionId,
        amount,
      })
      callbacks.onWithdrawalSubmitted?.(
        submissionId,
        depositAddressOrHash.kind === 'transaction'
          ? depositAddressOrHash.txHash
          : depositAddressOrHash.depositAddress,
      )
    },
    [apiKey, userId, config, sourceToken, destinationToken, callbacks],
  )

  const reset = useCallback(() => setSubmission({ status: 'idle' }), [])

  return { submission, submit, reset }
}
